import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Plus,
  Pin,
  PinOff,
  Trash2,
  Edit3,
  X,
  Bell,
  BellOff,
  Clock,
  Search,
  StickyNote,
  CheckCircle
} from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';
import { formatDateTime, formatRelative } from '../../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTE_COLORS = [
  { id: 'yellow', bg: 'bg-amber-50', border: 'border-amber-200', header: 'bg-amber-100', dot: 'bg-amber-400' },
  { id: 'blue', bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-100', dot: 'bg-blue-400' },
  { id: 'green', bg: 'bg-emerald-50', border: 'border-emerald-200', header: 'bg-emerald-100', dot: 'bg-emerald-400' },
  { id: 'pink', bg: 'bg-pink-50', border: 'border-pink-200', header: 'bg-pink-100', dot: 'bg-pink-400' },
  { id: 'purple', bg: 'bg-violet-50', border: 'border-violet-200', header: 'bg-violet-100', dot: 'bg-violet-400' },
  { id: 'orange', bg: 'bg-orange-50', border: 'border-orange-200', header: 'bg-orange-100', dot: 'bg-orange-400' },
];

const getColorClasses = (colorId) => NOTE_COLORS.find(c => c.id === colorId) || NOTE_COLORS[0];

const NoteModal = ({ note, onSave, onClose }) => {
  const [title, setTitle] = useState(note?.title || '');
  const [content, setContent] = useState(note?.content || '');
  const [color, setColor] = useState(note?.color || 'yellow');
  const [reminderAt, setReminderAt] = useState(note?.reminder_at ? note.reminder_at.slice(0, 16) : '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      title: title.trim() || 'Untitled Note',
      content,
      color,
      reminder_at: reminderAt ? new Date(reminderAt).toISOString() : null,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
          <h2 className="text-lg font-semibold text-[#111111]">
            {note ? 'Edit Note' : 'New Note'}
          </h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#111111]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-1.5">Title</label>
            <Input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Note title..."
              data-testid="note-title-input"
              className="border-[#E2E8F0]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#374151] mb-1.5">Content</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Write your note here..."
              rows={5}
              data-testid="note-content-input"
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#374151] mb-1.5">Color</label>
            <div className="flex gap-2">
              {NOTE_COLORS.map(c => (
                <button
                  key={c.id}
                  onClick={() => setColor(c.id)}
                  className={`w-8 h-8 rounded-full ${c.dot} ring-offset-2 transition-all ${color === c.id ? 'ring-2 ring-[#0D9488] scale-110' : 'hover:scale-105'}`}
                  data-testid={`color-${c.id}`}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#374151] mb-1.5">
              <Clock className="w-4 h-4 inline mr-1" />
              Reminder (optional)
            </label>
            <div className="flex gap-2">
              <input
                type="datetime-local"
                value={reminderAt}
                onChange={e => setReminderAt(e.target.value)}
                data-testid="note-reminder-input"
                className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
              />
              {reminderAt && (
                <Button variant="ghost" size="sm" onClick={() => setReminderAt('')} className="text-[#94A3B8]">
                  <BellOff className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-[#E2E8F0]">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-note-btn"
            className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
          >
            {saving ? 'Saving...' : note ? 'Update Note' : 'Create Note'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminNotes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const token = localStorage.getItem('admin_token');

  const fetchNotes = useCallback(async () => {
    try {
      const result = await safeFetch(`${API_URL}/api/admin/notes/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) {
        setNotes(result.data.notes || []);
      }
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleSave = async (noteData) => {
    try {
      if (editingNote) {
        await safeFetch(`${API_URL}/api/admin/notes/${editingNote.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(noteData)
        });
      } else {
        await safeFetch(`${API_URL}/api/admin/notes/`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(noteData)
        });
      }
      setShowModal(false);
      setEditingNote(null);
      fetchNotes();
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await safeFetch(`${API_URL}/api/admin/notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDeleteConfirm(null);
      fetchNotes();
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const handleTogglePin = async (noteId) => {
    try {
      await safeFetch(`${API_URL}/api/admin/notes/${noteId}/toggle-pin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchNotes();
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };

  const handleDismissReminder = async (noteId) => {
    try {
      await safeFetch(`${API_URL}/api/admin/notes/${noteId}/dismiss-reminder`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchNotes();
    } catch (err) {
      console.error('Failed to dismiss reminder:', err);
    }
  };

  const filteredNotes = notes.filter(n => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return n.title?.toLowerCase().includes(s) || n.content?.toLowerCase().includes(s);
  });

  const isReminderDue = (note) => {
    if (!note.reminder_at || note.reminder_dismissed) return false;
    return new Date(note.reminder_at) <= new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D9488]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Notes</h1>
          <p className="text-sm text-[#64748B] mt-1">{notes.length} note{notes.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search notes..."
              className="pl-9 border-[#E2E8F0]"
              data-testid="notes-search"
            />
          </div>
          <Button
            onClick={() => { setEditingNote(null); setShowModal(true); }}
            data-testid="create-note-btn"
            className="bg-[#0D9488] hover:bg-[#0F766E] text-white whitespace-nowrap"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Note
          </Button>
        </div>
      </div>

      {/* Due Reminders Banner */}
      {notes.some(isReminderDue) && (
        <Card className="bg-amber-50 border-amber-200 p-4">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-amber-600 animate-pulse" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">You have reminders due!</p>
              <div className="mt-1 space-y-1">
                {notes.filter(isReminderDue).map(n => (
                  <div key={n.id} className="flex items-center gap-2 text-sm text-amber-700">
                    <span className="font-medium">{n.title}</span>
                    <span className="text-amber-500">- due {formatDateTime(n.reminder_at)}</span>
                    <button
                      onClick={() => handleDismissReminder(n.id)}
                      className="ml-auto text-xs text-amber-600 hover:text-amber-800 underline"
                    >
                      Dismiss
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Notes Grid */}
      {filteredNotes.length === 0 ? (
        <div className="text-center py-16">
          <StickyNote className="w-16 h-16 text-[#CBD5E1] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#374151]">
            {searchTerm ? 'No notes match your search' : 'No notes yet'}
          </h3>
          <p className="text-[#64748B] text-sm mt-1 mb-4">
            {searchTerm ? 'Try a different search term' : 'Create your first sticky note to get started'}
          </p>
          {!searchTerm && (
            <Button
              onClick={() => { setEditingNote(null); setShowModal(true); }}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Note
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredNotes.map(note => {
            const colors = getColorClasses(note.color);
            const due = isReminderDue(note);

            return (
              <Card
                key={note.id}
                data-testid={`note-card-${note.id}`}
                className={`${colors.bg} ${colors.border} border shadow-sm hover:shadow-md transition-shadow relative group overflow-hidden`}
              >
                {/* Pin indicator */}
                {note.pinned && (
                  <div className="absolute top-2 right-2">
                    <Pin className="w-4 h-4 text-[#64748B] rotate-45" />
                  </div>
                )}

                {/* Header */}
                <div className={`px-4 py-3 ${colors.header} border-b ${colors.border}`}>
                  <h3 className="font-semibold text-[#111111] text-sm pr-6 line-clamp-1">{note.title}</h3>
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="text-sm text-[#374151] whitespace-pre-wrap line-clamp-6 min-h-[60px]">
                    {note.content || 'No content'}
                  </p>
                </div>

                {/* Reminder */}
                {note.reminder_at && (
                  <div className={`mx-4 mb-2 px-2.5 py-1.5 rounded-md text-xs flex items-center gap-1.5 ${
                    due ? 'bg-red-100 text-red-700' : 'bg-white/60 text-[#64748B]'
                  }`}>
                    {due ? <Bell className="w-3 h-3 animate-pulse" /> : <Clock className="w-3 h-3" />}
                    {due ? 'Due: ' : 'Reminder: '}{formatDateTime(note.reminder_at)}
                    {due && !note.reminder_dismissed && (
                      <button
                        onClick={() => handleDismissReminder(note.id)}
                        className="ml-auto"
                        title="Dismiss"
                      >
                        <CheckCircle className="w-3.5 h-3.5 text-green-600 hover:text-green-800" />
                      </button>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div className="px-4 py-2.5 border-t border-black/5 flex items-center justify-between">
                  <span className="text-[10px] text-[#94A3B8]">{formatRelative(note.updated_at)}</span>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleTogglePin(note.id)}
                      className="p-1.5 rounded hover:bg-black/5 text-[#64748B]"
                      title={note.pinned ? 'Unpin' : 'Pin'}
                      data-testid={`pin-note-${note.id}`}
                    >
                      {note.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => { setEditingNote(note); setShowModal(true); }}
                      className="p-1.5 rounded hover:bg-black/5 text-[#64748B]"
                      title="Edit"
                      data-testid={`edit-note-${note.id}`}
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(note.id)}
                      className="p-1.5 rounded hover:bg-red-50 text-[#64748B] hover:text-red-500"
                      title="Delete"
                      data-testid={`delete-note-${note.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Delete Confirmation */}
                {deleteConfirm === note.id && (
                  <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center p-4 rounded-lg">
                    <p className="text-sm font-medium text-[#111111] mb-3">Delete this note?</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                      <Button
                        size="sm"
                        onClick={() => handleDelete(note.id)}
                        className="bg-red-500 hover:bg-red-600 text-white"
                        data-testid="confirm-delete-btn"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <NoteModal
          note={editingNote}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingNote(null); }}
        />
      )}
    </div>
  );
};

export default AdminNotes;
